import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import useCurrencyService from '../../../../components/hooks/useCurrencyService';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { 
  DeleteSubscriptionPlanBIZsubs, 
  GetSubscriptionPlansBIZsubs, 
  UpdateSubscriptionPlanBIZsubs 
} from '../../../../redux/actions/business/subscriptions/subscriptionPlansActions';

import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import SubscriptionPlansModal from './SubscriptionPlansModal';

import global from "../../../../global.scss";

const SubscriptionPlansPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.subscriptions"]);
  const [loading, setLoading] = useState(true);
  const { handleShowAmount } = useCurrencyService();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [subscriptionPlansModalOpen, setSubscriptionPlansModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [subscriptionPlansList, setSubscriptionPlansList] = useState([]);
  const [subscriptionPlansTotal, setSubscriptionPlansTotal] = useState(0);

  const [subscriptionPlansMenuOpen, setSubscriptionPlansMenuOpen] = useState(false);
  const [subscriptionPlansMenuAnchor, setSubscriptionPlansMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Plan name",
      sort: "plan_name",
    },
    {
      label: "Monthly fee",
      sort: "monthly_fee",
    },
    {
      label: "Yearly fee",
      sort: "yearly_fee",
    },
    {
      label: "Number of free users",
      sort: "num_of_free_users",
    },
    {
      label: "Extra user monthly fee",
      sort: "extra_user_monthly_fee",
    },
     {
      label: "Free ACH monthly transfers",
      sort: "free_ach_monthly_transfers",
    },
    {
      label: "Tenant",
      sort: "ref_tenant_id",
    },
    {
      label: "Visible",
      sort: "is_visible",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "",
      sort: null,
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetSubscriptionPlans()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatSubscriptionPlansList(state.subscriptionPlansList.records || []);
      setSubscriptionPlansTotal(state.subscriptionPlansList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatSubscriptionPlansList = (list) => {
    setSubscriptionPlansList(list)
  };

  async function handleGetSubscriptionPlans(){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetSubscriptionPlansBIZsubs(page+1, rowsPerPage, query, setLoading));
  };

  const handleSubscriptionPlanToggle = (id, subscriptionPlan, bool) => {
    let obj = {
      ...subscriptionPlan,
      is_active: bool
    };
    dispatch(UpdateSubscriptionPlanBIZsubs(id, obj, setLoading)).then(() => {
      handleGetSubscriptionPlans()
    })
  };

  const handleSubscriptionPlanVisibleToggle = (id, subscriptionPlan, bool) => {
    let obj = {
      ...subscriptionPlan,
      is_visible: bool
    };
    dispatch(UpdateSubscriptionPlanBIZsubs(id, obj, setLoading)).then(() => {
      handleGetSubscriptionPlans()
    })
  };

  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteSubscriptionPlanBIZsubs(deleteId, setDeleting)).then(() => {
      handleGetSubscriptionPlans().then(() => {
        setDeleteId(null);
      })
    });
  }

  return (
    <Box>

      <SubscriptionPlansModal
        open={subscriptionPlansModalOpen}
        setOpen={setSubscriptionPlansModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetSubscriptionPlans}
        />

      <ConfirmationModal 
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"Subscription Plans"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Add Subscription plan"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Subscription plan");
          setSubscriptionPlansModalOpen(!subscriptionPlansModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          subscriptionPlansList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No subscription plan found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                subscriptionPlansList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.plan_name || <NA />}</TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={700}>
                        {handleShowAmount(row.currency, row.monthly_fee) || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={700}>
                        {handleShowAmount(row.currency, row.yearly_fee) || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.num_of_free_users || <NA />}</TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={700}>
                        {handleShowAmount(row.currency, row.extra_user_monthly_fee) || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {row.free_ach_monthly_transfers || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography>{row?.tenant?.legal_name || <NA />}</Typography>
                        <i><Typography>{row?.tenant.name || <NA />}</Typography></i>
                      </Box>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.ref_tenant_id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={row.is_visible}
                        onChange={(e) => {
                          handleSubscriptionPlanVisibleToggle(row.id, row, e.target.checked);
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={row.is_active}
                        onChange={(e) => {
                          handleSubscriptionPlanToggle(row.id, row, e.target.checked);
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            setSubscriptionPlansMenuAnchor(e.currentTarget);
                            setSubscriptionPlansMenuOpen(true);
                            setDeleteId(row.id);
                            setUpdate(row);
                            setModalTitle(row.plan_name || "");
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={subscriptionPlansTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={subscriptionPlansMenuOpen}
        anchorEl={subscriptionPlansMenuAnchor}
        onClose={() => setSubscriptionPlansMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setSubscriptionPlansMenuOpen(false);
            setSubscriptionPlansModalOpen(!subscriptionPlansModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSubscriptionPlansMenuOpen(false);
            setDeleteConfirmationModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <DeleteOutlineOutlinedIcon />
            Delete
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default SubscriptionPlansPage;